/**
 * <Checkbox> — la case à cocher contrôlée (atom).
 *
 * Une case et son libellé, qui presse avec elle. Toucher bascule (`onChange(next)`) ; la surface autour
 * (une `<Card>`, une feuille) reste à l'appelant — `<Autocomplete>` pose ses options dans des cartes.
 *
 * Quatre états, dans les deux versants (`CHECKBOX_FACES`, mesurés par `theme/contrast.test.ts`) :
 *  - **non cochée** : un contour seul, à l'encre secondaire ;
 *  - **cochée** : l'aplat d'une option choisie, la coche blanche dessus — elle ne se confond plus avec
 *    une case vide (daily du 23/10 : le carré plein recouvrait sa propre coche) ;
 *  - **au focus** (le clavier, sur le web) : l'anneau jaune du livre autour de la case (`<FocusRing>`) ;
 *    Espace la bascule, comme une case du navigateur ;
 *  - **désactivée** : tout s'estompe de moitié, comme `<Switch>`, et rien ne presse.
 * Pas d'état indéterminé : aucun écran n'en a besoin.
 *
 * Géométrie intrinsèque en nombres nus (la case, son contour, son rayon) : des proportions du dessin,
 * pas des décisions qu'un jeton porterait (contrat § 2).
 */
import React from "react"
import { Platform, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { FocusRing, focusRingHost } from "../FocusRing"
import { Icon } from "../icons/Icon"
import { Row } from "../containers/Row"
import { Text } from "../typography/Text"
import { CHECKBOX_FACES, type CheckboxFace } from "./checkboxFace"

/** La case — exportée pour la place que son squelette lui garde. */
export const CHECKBOX_GLYPH_SIZE = 20
const BORDER = 2
const RADIUS = 5
const CHECK = 14

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

export type CheckboxProps = {
    checked: boolean
    onChange: (next: boolean) => void
    /** Le libellé, à droite de la case : il fait partie de la zone qui presse. */
    label?: string
    disabled?: boolean
    testID?: string
}

/** La touche du clavier du web, telle que react-native-web la passe. */
type WebKeyEvent = { nativeEvent: { key: string; repeat?: boolean }; preventDefault: () => void }

export function Checkbox(props: CheckboxProps) {
    const { checked, onChange, label, disabled = false, testID } = props
    const face = CHECKBOX_FACES[checked ? "checked" : "unchecked"]
    // Au clavier du web, Espace coche, comme une case du navigateur : react-native-web ne presse qu'à
    // Entrée ce qui n'est pas un bouton.
    const webKeys =
        Platform.OS === "web"
            ? {
                  onKeyDown: (event: WebKeyEvent) => {
                      if (event.nativeEvent.key !== " " || disabled) return
                      event.preventDefault()
                      // Espace tenu ne la fait pas clignoter : une coche par appui.
                      if (event.nativeEvent.repeat === true) return
                      onChange(!checked)
                  },
              }
            : undefined

    return (
        <Pressable
            {...webKeys}
            testID={testID}
            disabled={disabled}
            onPress={() => onChange(!checked)}
            accessibilityRole="checkbox"
            accessibilityLabel={label}
            accessibilityState={{ checked, disabled }}
            // Le web ne lit pas `accessibilityState` : l'état coché s'y dit par l'attribut ARIA.
            aria-checked={checked}
            style={({ pressed }: PressableVisualState) => [
                styles.root(disabled),
                focusRingHost,
                pressed && !disabled ? styles.pressed : undefined,
            ]}
        >
            {({ focused }: PressableVisualState) => (
                <Row gap="2" align="center">
                    {/* L'anneau se pose sur la case, pas sur toute la ligne. */}
                    <View>
                        <FocusRing visible={focused === true && !disabled} radius={RADIUS} />
                        <View style={styles.box(face)}>
                            {face.check != null && <Icon name="check" size={CHECK} color={face.check} />}
                        </View>
                    </View>
                    {label != null && (
                        <Text size="text-base" weight="regular" color="text-primary" style={styles.label}>
                            {label}
                        </Text>
                    )}
                </Row>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: (disabled: boolean) => ({
        opacity: disabled ? 0.5 : 1,
        alignSelf: "stretch" as const,
    }),
    pressed: { opacity: 0.85 },
    box: (face: CheckboxFace) => ({
        width: CHECKBOX_GLYPH_SIZE,
        height: CHECKBOX_GLYPH_SIZE,
        borderRadius: RADIUS,
        borderWidth: BORDER,
        borderColor: theme.colors[face.border],
        backgroundColor: face.fill != null ? theme.colors[face.fill] : "transparent",
        alignItems: "center" as const,
        justifyContent: "center" as const,
    }),
    label: {
        flexShrink: 1,
    },
}))
